import React, { useEffect, useState, useCallback } from "react"
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import CircularProgress from "@mui/material/CircularProgress"
import { LoadingButton } from "@mui/lab"
import {
  Add,
  AutoAwesome,
  Delete,
  Download,
  PictureAsPdf,
  Send,
} from "@mui/icons-material"
import { Helmet } from "react-helmet"
import { useParams, useNavigate } from "react-router-dom"
import { useSnackbar } from "notistack"
import moment from "moment/moment"
import { api } from "../../../services/Main/Api"
import { getCondoId } from "../../../services/Main/AuthStorage"
import Masks from "../../../components/Shared/Masks"
import InspectionStatusChip from "./components/InspectionStatusChip"
import InspectionTypeChip from "./components/InspectionTypeChip"
import CharacteristicCard from "./components/CharacteristicCard"
import SignatorySection from "./components/SignatorySection"

const InspectionDetails = () => {
  const params = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeStep, setActiveStep] = useState(0)
  const [descriptions, setDescriptions] = useState({})

  // Loading states for actions
  const [aiLoading, setAiLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [signLoading, setSignLoading] = useState(false)

  // Add characteristic dialog
  const [addCharDialogOpen, setAddCharDialogOpen] = useState(false)
  const [newCharName, setNewCharName] = useState("")

  const isReadOnly =
    data?.status === "waiting" || data?.status === "signed"

  const characteristics = data?.characteristics || []
  const currentChar = characteristics[activeStep]
  const hasAnyImages = characteristics.some(
    (c) => c.images && c.images.length > 0
  )
  const hasSignatories = (data?.signatories || []).length > 0

  // Fetch inspection data
  const getInspection = useCallback(() => {
    const condo_id = getCondoId()
    api
      .get(`/property-inspection/${params.id}/`, {
        params: { condo_id },
      })
      .then((response) => {
        const inspectionData = response.data
        setData(inspectionData)

        // Initialize descriptions from server data
        const descs = {}
        ;(inspectionData.characteristics || []).forEach((c) => {
          descs[c.id] = c.description || ""
        })
        setDescriptions(descs)
      })
      .catch((error) => {
        console.log(error)
        enqueueSnackbar("Erro ao carregar vistoria.", { variant: "error" })
      })
      .finally(() => setLoading(false))
  }, [params.id])

  useEffect(() => {
    getInspection()
  }, [getInspection])

  // Save characteristic description
  function saveDescription(characteristicId, text) {
    const char = characteristics.find((c) => c.id === characteristicId)
    if (!char) return

    api
      .put(`/property-inspection/characteristic/${characteristicId}/`, {
        property_inspection: parseInt(params.id),
        name: char.name,
        description: text,
      })
      .then(() => {
        enqueueSnackbar("Descrição salva.", { variant: "success" })
      })
      .catch((error) => {
        console.log(error)
        enqueueSnackbar("Erro ao salvar descrição.", { variant: "error" })
      })
  }

  // Delete image
  function handleDeleteImage(imageId) {
    api
      .delete(`/property-inspection/characteristic/images/${imageId}/`)
      .then(() => {
        enqueueSnackbar("Imagem removida.", { variant: "info" })
        getInspection()
      })
      .catch((error) => {
        console.log(error)
        enqueueSnackbar("Erro ao remover imagem.", { variant: "error" })
      })
  }

  // Upload complete callback
  function handleImageUpload() {
    getInspection()
  }

  // Handle description change locally
  function handleDescriptionChange(characteristicId, text) {
    setDescriptions((prev) => ({ ...prev, [characteristicId]: text }))
  }

  // Generate AI descriptions
  function handleGenerateAI() {
    setAiLoading(true)
    api
      .post(`/property-inspection/${params.id}/generate-ai-description/`)
      .then((response) => {
        const results = response.data?.results || []
        const successCount = results.filter(
          (r) => r.status === "success"
        ).length
        const skippedCount = results.filter(
          (r) => r.status === "skipped"
        ).length
        const errorCount = results.filter((r) => r.status === "error").length

        let msg = `IA processou ${successCount} cômodo(s).`
        if (skippedCount > 0) msg += ` ${skippedCount} ignorado(s).`
        if (errorCount > 0) msg += ` ${errorCount} com erro.`

        enqueueSnackbar(msg, {
          variant: errorCount > 0 ? "warning" : "success",
        })
        getInspection()
      })
      .catch((error) => {
        console.log(error)
        enqueueSnackbar("Erro ao gerar descrições com IA.", {
          variant: "error",
        })
      })
      .finally(() => setAiLoading(false))
  }

  // Generate PDF
  function handleGeneratePDF() {
    setPdfLoading(true)
    api
      .post(`/property-inspection/${params.id}/generate-pdf/`)
      .then((response) => {
        enqueueSnackbar("Laudo PDF gerado com sucesso!", { variant: "success" })
        const fileUrl = response.data?.file_url
        if (fileUrl) {
          window.open(fileUrl, "_blank")
        }
        getInspection()
      })
      .catch((error) => {
        console.log(error)
        enqueueSnackbar("Erro ao gerar laudo PDF.", { variant: "error" })
      })
      .finally(() => setPdfLoading(false))
  }

  // Send for signing
  function handleSendForSigning() {
    if (
      !window.confirm(
        "Ao enviar para assinatura, a vistoria ficará somente leitura. Deseja continuar?"
      )
    )
      return

    setSignLoading(true)
    api
      .post(`/property-inspection/${params.id}/send-for-signing/`)
      .then(() => {
        enqueueSnackbar("Laudo enviado para assinatura!", {
          variant: "success",
        })
        getInspection()
      })
      .catch((error) => {
        console.log(error)
        enqueueSnackbar("Erro ao enviar para assinatura.", {
          variant: "error",
        })
      })
      .finally(() => setSignLoading(false))
  }

  // Add characteristic
  function handleAddCharacteristic() {
    if (!newCharName.trim()) return
    api
      .post("/property-inspection/characteristic/", {
        property_inspection: parseInt(params.id),
        name: newCharName.trim(),
      })
      .then(() => {
        enqueueSnackbar("Cômodo adicionado.", { variant: "success" })
        setNewCharName("")
        setAddCharDialogOpen(false)
        getInspection()
      })
      .catch((error) => {
        console.log(error)
        enqueueSnackbar("Erro ao adicionar cômodo.", { variant: "error" })
      })
  }

  // Delete characteristic
  function handleDeleteCharacteristic(charId) {
    if (!window.confirm("Deseja remover este cômodo e todas as suas fotos?"))
      return
    api
      .delete(`/property-inspection/characteristic/${charId}/`)
      .then(() => {
        enqueueSnackbar("Cômodo removido.", { variant: "info" })
        if (activeStep >= characteristics.length - 1 && activeStep > 0) {
          setActiveStep(activeStep - 1)
        }
        getInspection()
      })
      .catch((error) => {
        console.log(error)
        enqueueSnackbar("Erro ao remover cômodo.", { variant: "error" })
      })
  }

  // Stepper navigation
  const handleNext = () => {
    // Auto-save description on navigation
    if (currentChar && descriptions[currentChar.id] !== currentChar.description) {
      saveDescription(currentChar.id, descriptions[currentChar.id] || "")
    }
    if (activeStep < characteristics.length - 1) {
      setActiveStep(activeStep + 1)
    }
  }

  const handleBack = () => {
    if (currentChar && descriptions[currentChar.id] !== currentChar.description) {
      saveDescription(currentChar.id, descriptions[currentChar.id] || "")
    }
    if (activeStep > 0) {
      setActiveStep(activeStep - 1)
    }
  }

  // Loading state
  if (loading) {
    return (
      <Container maxWidth={false} sx={{ py: 3 }}>
        <Skeleton variant="rounded" height={150} sx={{ mb: 2 }} animation="pulse" />
        <Skeleton variant="rounded" height={500} animation="pulse" />
      </Container>
    )
  }

  if (!data) {
    return (
      <Container maxWidth={false} sx={{ py: 3 }}>
        <Typography>Vistoria não encontrada.</Typography>
        <Button onClick={() => navigate("/vistoria")} sx={{ mt: 2 }}>
          Voltar para lista
        </Button>
      </Container>
    )
  }

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <Helmet>
        <title>{data.number || "Vistoria"} - Bloco B3</title>
      </Helmet>

      <Backdrop sx={{ color: "#fff", zIndex: 1500 }} open={aiLoading}>
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2, color: "white" }}>
            Gerando descrições com IA...
          </Typography>
        </Box>
      </Backdrop>

      {/* Header Card */}
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="h6">{data.number}</Typography>
              <Typography variant="h6" color="text.secondary">
                —
              </Typography>
              <Typography variant="h6">{data.name}</Typography>
            </Box>
          }
          action={
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <InspectionTypeChip type={data.type_inspection} />
              <InspectionStatusChip status={data.status} />
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Grid container spacing={2}>
            {data.full_address && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Endereço:</strong> {data.full_address}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                <strong>CPF/CNPJ:</strong>{" "}
                {data.cpf_or_cnpj
                  ? Masks.cpfOrCnpj(data.cpf_or_cnpj)
                  : "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                <strong>Telefone:</strong> {data.phone || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                <strong>E-mail:</strong> {data.email || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                <strong>Criado em:</strong>{" "}
                {moment(data.created_at).format("DD/MM/YYYY [às] HH:mm")}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 3,
        }}
      >
        <LoadingButton
          variant="outlined"
          startIcon={<AutoAwesome />}
          loading={aiLoading}
          disabled={isReadOnly || !hasAnyImages || data.ai_processed}
          onClick={handleGenerateAI}
        >
          {data.ai_processed ? "IA já processada" : "Gerar Descrição IA"}
        </LoadingButton>

        <LoadingButton
          variant="outlined"
          startIcon={<PictureAsPdf />}
          loading={pdfLoading}
          disabled={isReadOnly}
          onClick={handleGeneratePDF}
        >
          Gerar Laudo PDF
        </LoadingButton>

        {data.file && hasSignatories && (
          <LoadingButton
            variant="outlined"
            startIcon={<Send />}
            loading={signLoading}
            disabled={isReadOnly}
            onClick={handleSendForSigning}
          >
            Enviar para Assinatura
          </LoadingButton>
        )}

        {data.file && (
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={() => window.open(data.file, "_blank")}
          >
            Baixar PDF
          </Button>
        )}
      </Box>

      {/* Stepper + Content */}
      <Grid container spacing={2}>
        {/* Left: Stepper */}
        <Grid item xs={12} md={3}>
          <Card elevation={3} sx={{ p: 2 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {characteristics.map((char, index) => (
                <Step key={char.id}>
                  <StepLabel
                    onClick={() => {
                      // Save current description before switching
                      if (
                        currentChar &&
                        descriptions[currentChar.id] !== currentChar.description
                      ) {
                        saveDescription(
                          currentChar.id,
                          descriptions[currentChar.id] || ""
                        )
                      }
                      setActiveStep(index)
                    }}
                    sx={{ cursor: "pointer" }}
                    optional={
                      !isReadOnly && (
                        <Tooltip title="Remover cômodo">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteCharacteristic(char.id)
                            }}
                          >
                            <Delete fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  >
                    {char.name}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {!isReadOnly && (
              <Box sx={{ mt: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setAddCharDialogOpen(true)}
                >
                  Adicionar Cômodo
                </Button>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Right: Content */}
        <Grid item xs={12} md={9}>
          {characteristics.length > 0 && currentChar ? (
            <CharacteristicCard
              characteristic={currentChar}
              inspectionNumber={data.number}
              readOnly={isReadOnly}
              description={descriptions[currentChar.id] || ""}
              onDescriptionChange={(text) =>
                handleDescriptionChange(currentChar.id, text)
              }
              onImageUpload={handleImageUpload}
              onImageDelete={handleDeleteImage}
              onBack={handleBack}
              onNext={handleNext}
              isFirst={activeStep === 0}
              isLast={activeStep === characteristics.length - 1}
              onSaveDescription={saveDescription}
            />
          ) : (
            <Card>
              <CardContent sx={{ textAlign: "center", py: 6 }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Nenhum cômodo cadastrado.
                </Typography>
                {!isReadOnly && (
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => setAddCharDialogOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Adicionar Cômodo
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Signatories Section */}
      <SignatorySection
        inspectionId={params.id}
        signatories={data.signatories || []}
        onRefresh={getInspection}
        readOnly={isReadOnly}
      />

      {/* Add Characteristic Dialog */}
      <Dialog
        open={addCharDialogOpen}
        onClose={() => setAddCharDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Adicionar Cômodo</DialogTitle>
        <Divider />
        <DialogContent>
          <TextField
            label="Nome do cômodo"
            fullWidth
            size="small"
            value={newCharName}
            onChange={(e) => setNewCharName(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddCharacteristic()
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddCharDialogOpen(false)} color="secondary">
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAddCharacteristic}
            disabled={!newCharName.trim()}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default InspectionDetails
